import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {
  Box,
  Card,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Controller, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import styles from '../../assets/styles/userDashboard';
import CustomButton from '../../components/Button/button';
import CustomTextField from '../../components/TextField/textField';
import CustomTypography from '../../components/Typography/typography';
import {
  financialYearOptions,
  initialState,
  payFreqArray,
} from '../../constants';
import {
  calculationApi,
  superannuationApi,
  unitsCalculationApi,
  userSliceActions,
} from '../../redux/slice/userSlice';

const fields = ['gross', 'netAmountPaid', 'taxWithdeld'];

const DashBoard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const unitsCalculationData = useSelector(
    (state: any) => state.user.unitsCalculationData
  );
  const calculationData = useSelector(
    (state: any) => state.user.calculationData
  );
  const superannutationData = useSelector(
    (state: any) => state.user.superannutationData
  );

  const form = useForm({
    defaultValues: initialState,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    control,
    formState,
    setValue,
    reset,
    getValues,
    watch,
    resetField,
  } = form;
  const { errors, isDirty, dirtyFields } = formState;
  const classes = styles();

  //submit method that will call calculation api
  const onSubmit = (data: FieldValues) => {
    const obj = {
      field: '',
      data: '',
      payFreq: data.payFreqArray,
      financialYear: data.financialYear,
      taxFreeThresholdClaimed: data.taxFreeThresholdClaimed,
      studyOrTrainingLoan: data.studyOrTrainingLoan,
    };

    if (
      data.gross === '' &&
      data.netAmountPaid === '' &&
      data.taxWithdeld === ''
    ) {
      return;
    } else {
      if (data.gross !== '' && dirtyFields['gross']) {
        const datas = { ...obj, field: 'gross', data: data.gross };

        dispatch(calculationApi({ datas, navigate }));
        resetFieldsTouch('gross');
      } else if (data.netAmountPaid !== '' && dirtyFields['netAmountPaid']) {
        const datas = {
          ...obj,
          field: 'netAmountPaid',
          data: data.netAmountPaid,
        };

        dispatch(calculationApi({ datas, navigate }));
        resetFieldsTouch('netAmountPaid');
      } else {
        const datas = { ...obj, field: 'taxWithdeld', data: data.taxWithdeld };

        dispatch(calculationApi({ datas, navigate }));
        resetFieldsTouch('taxWithdeld');
      }
    }
  };

  function resetFieldsTouch(dirtyField: string) {
    // reset all other fields to pristine
    fields.forEach((f) => {
      if (f !== dirtyField) {
        const fieldName =
          f === 'taxWithdeld'
            ? 'taxWithdeld'
            : f === 'gross'
            ? 'gross'
            : f === 'netAmountPaid'
            ? 'netAmountPaid'
            : '';
        if (fieldName) resetField(fieldName, { keepDirty: false });
      }
    });
  }

  const unitsCalculationSubmit = () => {
    const [grossForUnits, unitsperday, rateperunit, financialYear] = getValues([
      'grossForUnits',
      'unitsperday',
      'rateperunit',
      'financialYear',
    ]);
    grossForUnits !== '' &&
      (unitsperday !== '' || rateperunit !== '') &&
      dispatch(
        unitsCalculationApi({
          data: { grossForUnits, unitsperday, rateperunit, financialYear },
          navigate,
        })
      );
  };

  const handleResetButton = (type: string) => {
    if (type === 'all') {
      reset(initialState);
      dispatch(userSliceActions.clearCalculationData());
    } else {
      setValue('unitsperday', initialState.unitsperday);
      setValue('rateperunit', initialState.rateperunit);
    }
    dispatch(userSliceActions.clearUnitsCalculationData());
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (e.target.name == 'taxWithdeld') {
      value = value.charAt(0) === '0' ? value.replace(value, '') : value;
      value =
        /[^0-9]/.test(value) === true ? value.replaceAll(/[^0-9]/g, '') : value;
      value =
        Number(value) >= 10000000 ? value.replace(value, '10000000') : value;

      return value;
    } else {
      value = value.charAt(0) === '0' ? value.replace(value, '') : value;
      // value =
      //   /[^0-9]/.test(value) === true ? value.replaceAll(/[^0-9]/g, '') : value;
      value =
        Number(value) >= 10000000 ? value.replace(value, '10000000') : value;
      return value;
    }
  };

  const handleFinancialYearClick = (e: SelectChangeEvent<unknown>) => {
    // const grossvalue = getValues('gross');
    // const financialYear = getValues('financialYear');
    // grossvalue !== '' &&
    //   dispatch(
    //     superannuationApi({ data: { grossvalue, financialYear }, navigate })
    //   );
    formRef.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true })
    );
  };

  //function to call when value of Pay Frequency will change that will call calculation api
  const handlePayFrequencyChange = (e: SelectChangeEvent<unknown>) => {
    formRef.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true })
    );
  };

  //function to call when value of radio button will change that will call calculation api
  const handleRadioChange = () => {
    formRef.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true })
    );
  };

  const onError = (errors: FieldErrors) => {};

  useEffect(() => {
    calculationData?.result &&
      Object.keys(calculationData?.result).map((item: any) =>
        setValue(item, calculationData?.result[item])
      );

    unitsCalculationData &&
      Object.keys(unitsCalculationData).map((item: any) =>
        setValue(item, unitsCalculationData[item])
      );

    superannutationData && setValue('superannuation', superannutationData);

    !isDirty && reset(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationData, unitsCalculationData, superannutationData]);

  return (
    <>
      <div className={classes.Container}>
        <Box className={classes.boxContainer}>
          <form
            className={classes.formContainer}
            ref={formRef}
            onSubmit={handleSubmit((data) => onSubmit(data), onError)}
          >
            <Card elevation={6} className={classes.paper}>
              <div className={classes.formHeading}>
                <h2 className={classes.h2heading}>
                  {'Gross, Net or Tax Witheld Calculator'}
                </h2>
              </div>
              <Divider />

              <Grid container spacing={1} className={classes.typography}>
                <Grid item xs={12} sm={12} md={12}>
                  <Box className={classes.fieldContainer}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      className={classes.Financialyear}
                    >
                      <Controller
                        name="financialYear"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControl
                              className={classes.Financial}
                              size="small"
                            >
                              <InputLabel
                                id="outlined-basic"
                                className={classes.selectLabel}
                              >
                                Financial year
                              </InputLabel>
                              <Select
                                {...field}
                                id="outlined-basic"
                                label="Financial year"
                                variant="outlined"
                                className={classes.selectInput}
                                onChange={(e) => {
                                  field.onChange(e);
                                  handleFinancialYearClick(e);
                                }}
                              >
                                {financialYearOptions.map((item) => (
                                  <MenuItem
                                    value={item.label}
                                    key={item.label}
                                    className={classes.menuClass}
                                  >
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      className={classes.Financialyear}
                    >
                      <Controller
                        name="payFreqArray"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControl
                              className={classes.Financial}
                              size="small"
                            >
                              <InputLabel
                                id="outlined-basic"
                                className={classes.selectLabel}
                              >
                                Pay frequency
                              </InputLabel>
                              <Select
                                {...field}
                                id="outlined-basic"
                                label="Pay frequency"
                                variant="outlined"
                                className={classes.selectInput}
                                onChange={(e) => {
                                  field.onChange(e);
                                  handlePayFrequencyChange(e);
                                }}
                              >
                                {payFreqArray.map((item) => (
                                  <MenuItem
                                    value={item.value}
                                    key={item.value}
                                    className={classes.menuClass}
                                  >
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        )}
                      />
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={1} className={classes.gridContainer}>
                <div className={classes.radioButtonContainer}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    className={classes.innerGrid}
                  >
                    <Controller
                      name="taxFreeThresholdClaimed"
                      control={control}
                      render={({ field }) => (
                        <Box className={classes.ContainerP}>
                          <CustomTypography
                            id="demo-radio-buttons-group-label"
                            className={classes.headingLabel}
                          >
                            Tax free threshold claimed{' '}
                          </CustomTypography>
                          <RadioGroup
                            className={classes.ContainerPDiv}
                            aria-labelledby="demo-radio-buttons-group-label"
                            {...field}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              field.onChange(e);
                              handleRadioChange();
                            }}
                            name="taxFreeThresholdClaimed"
                          >
                            <FormControlLabel
                              value="yes"
                              classes={{ label: classes.checkBoxLabel }}
                              className={classes.labelContainer}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="no"
                              classes={{ label: classes.checkBoxLabel }}
                              className={classes.labelContainer}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    className={classes.innerGrid}
                  >
                    <Controller
                      name="studyOrTrainingLoan"
                      control={control}
                      render={({ field }) => (
                        <Box
                          flexDirection={'row'}
                          className={classes.ContainerP}
                        >
                          <CustomTypography
                            id="demo-radio-buttons-group-label"
                            className={classes.headingLabel}
                          >
                            Study or training loan{' '}
                          </CustomTypography>
                          <RadioGroup
                            className={classes.ContainerPDiv}
                            aria-labelledby="demo-radio-buttons-group-label"
                            {...field}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              field.onChange(e);
                              handleRadioChange();
                            }}
                            name="studyOrTrainingLoan"
                          >
                            <FormControlLabel
                              value="yes"
                              classes={{ label: classes.checkBoxLabel }}
                              className={classes.labelContainer}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="no"
                              classes={{ label: classes.checkBoxLabel }}
                              className={classes.labelContainer}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </Box>
                      )}
                    />
                  </Grid>
                </div>
              </Grid>

              <Grid container className={classes.typography} spacing={1}>
                <Grid item xs={12} sm={12} md={12}>
                  <Box className={classes.fieldContainer}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      className={classes.innerContainer}
                    >
                      <Controller
                        name={'gross'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter gross'}
                                size="small"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(
                                    onChange(e),
                                    setValue('netAmountPaid', ''),
                                    setValue('taxWithdeld', '')
                                  );
                                }}
                                className={classes.textInput}
                              />
                              <div className={classes.dollarIconContainer}>
                                <AttachMoneyIcon />
                              </div>
                            </div>
                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.gross &&
                                errors.gross?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                        rules={{
                          validate: {
                            whitespace: (fieldValue: string) => {
                              return (
                                /^[ \t]+/.test(fieldValue) !== true ||
                                "gross can't be whitespace"
                              );
                            },
                          },
                        }}
                      />

                      <Controller
                        name={'netAmountPaid'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter net amount paid'}
                                size="small"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(
                                    onChange(e),
                                    setValue('taxWithdeld', ''),
                                    setValue('gross', '')
                                  );
                                }}
                                // disabled={true}
                                className={classes.textInput}
                              />
                              <div className={classes.dollarIconContainer}>
                                <AttachMoneyIcon />
                              </div>
                            </div>

                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.netAmountPaid?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                        rules={{
                          validate: {
                            whitespace: (fieldValue: string) => {
                              return (
                                /^[ \t]+/.test(fieldValue) !== true ||
                                "net amount paid can't be whitespace"
                              );
                            },
                          },
                        }}
                      />

                      <Controller
                        name={'taxWithdeld'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter tax withdeld'}
                                size="small"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(
                                    onChange(e),
                                    setValue('netAmountPaid', ''),
                                    setValue('gross', '')
                                  );
                                }}
                                className={classes.textInput}
                                // disabled={true}
                              />
                              <div className={classes.dollarIconContainer}>
                                <AttachMoneyIcon />
                              </div>
                            </div>

                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.taxWithdeld?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                        rules={{
                          validate: {
                            whitespace: (fieldValue: string) => {
                              return (
                                /^[ \t]+/.test(fieldValue) !== true ||
                                "tax with held can't be whitespace"
                              );
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid
                      item
                      md={4}
                      xs={12}
                      sm={8}
                      className={classes.buttonContainer}
                    >
                      <CustomButton
                        type="submit"
                        color="primary"
                        size="medium"
                        // disabled={isDisable ? true : false}
                        onSubmit={() => handleSubmit((data) => onSubmit(data))}
                        className={classes.button}
                      >
                        Calculate
                      </CustomButton>
                      <CustomButton
                        type="button"
                        color="#ff0000"
                        size="medium"
                        onClick={() => handleResetButton('all')}
                        className={`${classes.button} `}
                      >
                        Reset All
                      </CustomButton>
                    </Grid>
                  </Box>

                  {/* calculation results table */}
                  {/* {calculationData?.result?.responseObj?.length > 0 && (
										<CalculationTable
											calculationData={calculationData?.result?.responseObj}
										/>
									)} */}
                </Grid>
              </Grid>
            </Card>

            <Card elevation={6} className={classes.paper}>
              <div className={classes.formHeading}>
                <h2 className={classes.h2heading}>
                  {'Units or Rate Calculator'}
                </h2>
              </div>
              <Divider />

              <Grid container className={classes.typography} spacing={1}>
                <Grid item xs={12} sm={12} md={12}>
                  <CustomTypography
                    variant="body2"
                    className={classes.headingLabel}
                  >
                    Enter data into any one field
                  </CustomTypography>
                  <Box className={classes.fieldContainer}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      className={classes.innerContainer}
                    >
                      <Controller
                        name={'grossForUnits'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter gross'}
                                size="small"
                                disabled={true}
                                className={classes.textInput}
                              />
                              <div className={classes.dollarIconContainer}>
                                <AttachMoneyIcon />
                              </div>
                            </div>
                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.grossForUnits &&
                                errors.grossForUnits?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                      />

                      <Controller
                        name={'unitsperday'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter units per pay'}
                                size="small"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(
                                    onChange(e),
                                    setValue('rateperunit', '')
                                  );
                                }}
                                className={classes.textInput}
                              />
                            </div>
                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.unitsperday?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                        rules={{
                          validate: {
                            whitespace: (fieldValue: string) => {
                              return (
                                /^[ \t]+/.test(fieldValue) !== true ||
                                "units per day can't be whitespace"
                              );
                            },
                            // specialCharacter: (fieldValue: string) => {
                            //   return (
                            //     (/[^0-9 ]/.test(fieldValue) !== true &&
                            //       fieldValue.length < 2) ||
                            //     'Please enter valid value'
                            //   );
                            // },
                            leadingZero: (fieldValue: string) => {
                              return (
                                fieldValue.charAt(0) !== '0' ||
                                'Please enter valid value'
                              );
                            },
                          },
                        }}
                      />

                      <Controller
                        name={'rateperunit'}
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.textField}>
                            <div className={classes.textInputContainer}>
                              <CustomTextField
                                {...field}
                                label={'Enter rate per unit'}
                                size="small"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(
                                    onChange(e),
                                    setValue('unitsperday', '')
                                  );
                                }}
                                className={classes.textInput}
                              />
                              <div className={classes.dollarIconContainer}>
                                <AttachMoneyIcon />
                              </div>
                            </div>
                            <CustomTypography
                              variant="body2"
                              color="red"
                              align="justify"
                            >
                              {errors.rateperunit?.message?.toString()}
                            </CustomTypography>
                          </Box>
                        )}
                        rules={{
                          validate: {
                            whitespace: (fieldValue: string) => {
                              return (
                                /^[ \t]+/.test(fieldValue) !== true ||
                                "rate per unit can't be whitespace"
                              );
                            },
                            leadingZero: (fieldValue: string) => {
                              return (
                                fieldValue.charAt(0) !== '0' ||
                                'Please enter valid value'
                              );
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid
                      item
                      md={4}
                      xs={12}
                      sm={8}
                      className={classes.buttonContainer}
                    >
                      <CustomButton
                        color="primary"
                        size="medium"
                        onClick={unitsCalculationSubmit}
                        className={classes.button}
                      >
                        Calculate
                      </CustomButton>
                      <CustomButton
                        type="button"
                        color="#ff0000"
                        size="medium"
                        onClick={() => handleResetButton('unit')}
                        className={`${classes.button} ${classes.customButtonClass}`}
                      >
                        Reset
                      </CustomButton>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            <Card elevation={6} className={classes.paper}>
              <div className={classes.formHeading}>
                <h2 className={classes.h2heading}>
                  {'Superannuation Calculator'}
                </h2>
              </div>
              <Divider />

              <Grid container className={classes.typography} spacing={1}>
                <Grid item xs={12} sm={12} md={12}>
                  <Box className={classes.fieldContainer}>
                    <Controller
                      name={'grossForSuperannuation'}
                      control={control}
                      render={({ field }) => (
                        <Box className={classes.textField}>
                          <div className={classes.textInputContainer}>
                            <CustomTextField
                              {...field}
                              label={'Gross'}
                              size="small"
                              disabled={true}
                              className={classes.textInput}
                            />
                            <div className={classes.dollarIconContainer}>
                              <AttachMoneyIcon />
                            </div>
                          </div>
                        </Box>
                      )}
                    />

                    <Controller
                      name={'superannuation'}
                      control={control}
                      render={({ field }) => (
                        <Box className={classes.textField}>
                          <div className={classes.textInputContainer}>
                            <CustomTextField
                              {...field}
                              label={'Superannuation'}
                              size="small"
                              disabled={true}
                              className={classes.textInput}
                            />
                            <div className={classes.dollarIconContainer}>
                              <AttachMoneyIcon />
                            </div>
                          </div>
                        </Box>
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </form>
        </Box>
      </div>
    </>
  );
};

export default DashBoard;
